import type { WatchedExpansionDto } from "@repo/game-admin"

/** How complete our catalogue is next to what TCGplayer lists. */
export type CompletionState =
	| "complete"
	| "partial"
	| "missing"
	| "not_on_tcgplayer"
	| "no_tcgplayer_data"

/** The fields of a watched expansion completion is computed from. */
export type CompletionInput = Pick<
	WatchedExpansionDto,
	"prodCardCount" | "prodSealedCount" | "tcgplayer"
>

export interface Completion {
	state: CompletionState
	ours: number
	theirs: number
}

export const COMPLETION_TONES: Record<CompletionState, string> = {
	complete: "border-transparent bg-ok-soft text-ok",
	partial: "border-transparent bg-warn-soft text-warn",
	missing: "border-transparent bg-bad-soft text-bad",
	not_on_tcgplayer: "border-transparent bg-foreground/8 text-muted-foreground",
	no_tcgplayer_data: "border-transparent text-muted-foreground",
}

/** Dot colour of a state, for compact places such as calendar entries. */
export const COMPLETION_DOT_TONES: Record<CompletionState, string> = {
	complete: "bg-ok",
	partial: "bg-warn",
	missing: "bg-bad",
	not_on_tcgplayer: "bg-meta",
	no_tcgplayer_data: "bg-meta",
}

function stateOf(ours: number, theirs: number): CompletionState {
	if (theirs === 0) return "not_on_tcgplayer"
	if (ours >= theirs) return "complete"
	if (ours === 0) return "missing"
	return "partial"
}

/** Our prod singles next to the singles TCGplayer lists. */
export function singlesCompletion(row: CompletionInput): Completion {
	if (!row.tcgplayer) {
		return { state: "no_tcgplayer_data", ours: row.prodCardCount, theirs: 0 }
	}

	const { ourSingleCount, singleCount } = row.tcgplayer
	return {
		state: stateOf(ourSingleCount, singleCount),
		ours: ourSingleCount,
		theirs: singleCount,
	}
}

/** How many of TCGplayer's sealed products our catalogue carries, by TCGplayer id. */
export function sealedCompletion(row: CompletionInput): Completion {
	if (!row.tcgplayer) {
		return { state: "no_tcgplayer_data", ours: row.prodSealedCount, theirs: 0 }
	}

	const { sealedInCatalogueCount, sealedCount } = row.tcgplayer
	return {
		state: stateOf(sealedInCatalogueCount, sealedCount),
		ours: sealedInCatalogueCount,
		theirs: sealedCount,
	}
}

const STATE_ORDER: Record<CompletionState, number> = {
	missing: 0,
	partial: 1,
	complete: 2,
	not_on_tcgplayer: 3,
	no_tcgplayer_data: 4,
}

/** Sort key putting the least complete first, then what TCGplayer does not list. */
export function completionSortKey(completion: Completion): number {
	const ratio =
		completion.theirs > 0 ? Math.min(completion.ours / completion.theirs, 1) : 1
	return STATE_ORDER[completion.state] + ratio / 2
}
